import { Injectable, inject } from '@angular/core';

import { BetslipBarConfig } from '@frontend/sports/common/client-config-data-access';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { SportsProductService } from '@frontend/sports/host-app/sports-product/feature/sports-product-service';
import { DslService, HtmlNode, ViewTransitionUtils } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, map, startWith, tap } from 'rxjs';

import { betslipPicksListCountSelector } from '../../picks/selectors';

@Injectable({ providedIn: 'root' })
export class BetslipBarVisibilityService {
    readonly #viewTransitionUtils = inject(ViewTransitionUtils);

    private readonly visibilitySubject = new BehaviorSubject(true);
    private readonly isSportsActive$ = inject(SportsProductService).isSportsProductActive$;

    private readonly BETSLIP_BAR_VISIBLE_CLASS = 'betslip-bar-visible';

    private readonly htmlNodeService = inject(HtmlNode);
    private readonly betslipBarConfig = inject(BetslipBarConfig);

    private readonly hasPicks$ = inject(Store)
        .select(betslipPicksListCountSelector)
        .pipe(
            startWith(0),
            map((count) => count > 0),
        );

    private readonly excludedRoutes$ = inject(RouterEventsService).currentActivationEnd.pipe(
        map((route) => route?.snapshot.data?.tag === RouteTag.MyBets),
    );

    readonly enableDsl$ = inject(DslService).evaluateExpression<boolean>(this.betslipBarConfig.isEnabledDsl);

    readonly componentVisible$ = combineLatest([
        this.visibilitySubject,
        this.enableDsl$,
        this.excludedRoutes$,
        this.hasPicks$,
        this.isSportsActive$,
    ]).pipe(
        this.#viewTransitionUtils.deferUntilTransitionReady({ async: true }),
        map(
            ([isVisible, enabled, onExcludedRoute, hasPicks, isSportsActive]) =>
                isSportsActive && enabled && isVisible && !onExcludedRoute && (!this.betslipBarConfig.hideOnEmptyBetslip || hasPicks),
        ),
        tap((visible) => {
            this.htmlNodeService.setCssClass(this.BETSLIP_BAR_VISIBLE_CLASS, visible);
        }),
    );

    hideComponent(): void {
        this.visibilitySubject.next(false);
    }

    showComponent(): void {
        this.visibilitySubject.next(true);
    }
}
